// src/services/dataAdapters/DataAdapter.js
// Abstract base class / interface for all DataAdapters.
//
// Every concrete adapter (MockDataAdapter, SupabaseDataAdapter, …) MUST
// override each method below.  Calling an un-overridden method throws so
// that omitted implementations are caught early and clearly.

class DataAdapter {
  /**
   * The data source type identifier (e.g. 'mock', 'supabase', 'postgres').
   * @returns {string}
   */
  get type() {
    throw new Error(`${this.constructor.name} must implement get type()`);
  }

  /**
   * Test connectivity to the data source.
   * @returns {Promise<{ success: boolean, latencyMs: number, error?: string }>}
   */
  async ping() {
    throw new Error(`${this.constructor.name} must implement ping()`);
  }

  /**
   * List all tables (or views / collections) available in this data source.
   * @returns {Promise<string[]>}
   */
  async listTables() {
    throw new Error(`${this.constructor.name} must implement listTables()`);
  }

  /**
   * List columns for a given table.
   * @param {string} table
   * @returns {Promise<Array<{ name: string, type: string, nullable?: boolean }>>}
   */
  // eslint-disable-next-line no-unused-vars
  async listColumns(table) {
    throw new Error(`${this.constructor.name} must implement listColumns(table)`);
  }

  /**
   * Execute a query against the data source.
   *
   * @param {{
   *   table:    string,
   *   columns?: string[],
   *   filter?:  object,
   *   limit?:   number,
   * }} params
   * @returns {Promise<object[]>}
   */
  // eslint-disable-next-line no-unused-vars
  async query({ table, columns, filter, limit } = {}) {
    throw new Error(
      `${this.constructor.name} must implement query({table, columns, filter, limit})`
    );
  }

  /**
   * Count rows matching an optional filter.
   *
   * @param {{
   *   table:   string,
   *   filter?: object,
   * }} params
   * @returns {Promise<number>}
   */
  // eslint-disable-next-line no-unused-vars
  async count({ table, filter } = {}) {
    throw new Error(
      `${this.constructor.name} must implement count({table, filter})`
    );
  }

  /**
   * Describe which schema-introspection capabilities this adapter provides.
   *
   * @returns {Promise<{
   *   canListTables:      boolean,
   *   canListColumns:     boolean,
   *   canIntrospectTypes: boolean,
   * }>}
   */
  async schemaCapabilities() {
    throw new Error(`${this.constructor.name} must implement schemaCapabilities()`);
  }
}

export default DataAdapter;
